import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {VetClinicService} from './vet-clinic.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard weryfikujący uprawnienia administracyjne użytkownika.
 * Zabezpiecza moduły i ścieżki (Routes) przed nieautoryzowanym dostępem.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private vetClinicService: VetClinicService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) całego modułu
   * (paczki JavaScript) z serwera. Chroni przed pobraniem samego kodu źródłowego
   * panelu administracyjnego przez osoby nieuprawnione, oszczędzając jednocześnie
   * transfer danych w przeglądarce klienta.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/admin-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }), map(response => {
        console.log('check - admin - ok');
        return true;
      }));
  }

  /**
   * Blokuje lub zezwala na aktywację i wyrenderowanie konkretnego komponentu/widoku,
   * którego kod znajduje się już w pamięci przeglądarki. W przypadku braku autoryzacji
   * (HTTP 401 Unauthorized), natychmiast przekierowuje na ekran logowania
   * i niszczy aktualną sesję.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/admin-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }),
      map(response => {
        console.log('check - canActivate - admin - ok', response);

        return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
      }));
  }

}
